import type { CSSProperties } from 'react';

/**
 * Shared animation class strings for consistent motion across the design system.
 *
 * Three patterns cover the common cases:
 *
 * - `overlayAnimation`  — Radix overlay backdrop (modal, dialog) — fade only
 * - `panelAnimation`    — Radix panel content (modal, dialog) — fade, open 300ms / close 200ms
 * - `floatInAnimation`  — Mount-based floating elements (bulk action bar, toasts) — fade + subtle upward drift
 *
 * Usage:
 * ```tsx
 * import { overlayAnimation, panelAnimation, floatInAnimation } from '@wayste/shadcn/lib/animations';
 *
 * // Radix overlay
 * <DialogPrimitive.Overlay className={cn(overlayAnimation, 'fixed inset-0 bg-black/50')} />
 *
 * // Radix panel content
 * <DialogPrimitive.Content className={cn(panelAnimation, '...')} style={panelAnimationStyle} />
 *
 * // Floating element (mounts/unmounts directly)
 * <div className={cn(floatInAnimation, 'fixed bottom-6 left-1/2')} />
 * ```
 */

/** Fade in/out for Radix overlay backdrops. */
export const overlayAnimation =
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0';

/** Fade in/out for Radix panel content. Open 300ms, close 200ms, ease-out. */
export const panelAnimation =
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 ease-out data-[state=closed]:duration-200 data-[state=open]:duration-300';

/**
 * Inline style to apply alongside `panelAnimation` on centered Radix panels.
 * Overrides tailwindcss-animate's enter/exit translate so the centering
 * `-translate-x-1/2 -translate-y-1/2` transform is preserved during animation,
 * and the panel enters from 1rem below center rather than from the corner.
 */
export const panelAnimationStyle = {
    '--tw-enter-translate-x': '-50%',
    '--tw-enter-translate-y': 'calc(-50% + 1rem)',
    '--tw-exit-translate-x': '-50%',
    '--tw-exit-translate-y': 'calc(-50% + 1rem)',
} as CSSProperties;

/**
 * Fade + subtle upward drift for mount-based floating elements with no exit animation.
 * Use when the element is immediately removed from the DOM on close (no exit needed).
 */
export const floatInAnimation = 'animate-in fade-in-0 slide-in-from-bottom-2 duration-300 ease-out';

/**
 * Fade-only animation for non-Radix floating elements — matches the modal's feel.
 * Apply `data-state="open"|"closed"` to the element and handle DOM removal in `onAnimationEnd`.
 * Use alongside `floatAnimationStyle` for the subtle upward drift.
 * Matches modal timing: open 300ms / close 200ms, ease-out.
 *
 * @example
 * ```tsx
 * <div
 *   data-state={isVisible ? 'open' : 'closed'}
 *   onAnimationEnd={() => { if (!isVisible) setShouldRender(false); }}
 *   className={cn(floatAnimation, 'fixed bottom-6 left-1/2 -translate-x-1/2 ...')}
 *   style={floatAnimationStyle}
 * />
 * ```
 */
export const floatAnimation =
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 ease-out data-[state=closed]:duration-200 data-[state=open]:duration-300';

/**
 * Inline style to apply alongside `floatAnimation` on bottom-centered floating elements.
 * Preserves the `-translate-x-1/2` centering transform during animation and adds a
 * subtle 8px upward drift — matching the modal's entrance feel.
 */
export const floatAnimationStyle = {
    '--tw-enter-translate-x': '-50%',
    '--tw-exit-translate-x': '-50%',
    '--tw-enter-translate-y': '0.5rem',
    '--tw-exit-translate-y': '0.5rem',
} as CSSProperties;
